/**
 * Bölge Araç Yoklaması + Bölge Araç Ataması — FE tipleri.
 * Kaynak: docs/BOLGE_ARAC_YOKLAMA_API.md v1.2 §1 (birebir kopya; BE bu şekilleri döner).
 * Plan: docs/BOLGE_ARAC_YOKLAMA_PLANI.md v1.3 (öncelik §13 > §12 > §11).
 * Bilinmeyen sayısal değer `null` gelir — ekranda "—" gösterilir, asla 0 yazılmaz.
 */

// ───────────────────────── §1 Ortak tipler ─────────────────────────
export type DayStr = string; // "YYYY-MM-DD"
export type RegionRole = 'DAGITIM' | 'TOPLAMA' | 'GIDEN_TRANSFER' | 'GELEN_TRANSFER';
export type MembershipRole = 'ATANMIS' | 'MISAFIR' | 'BAGIMSIZ' | 'ATANMAMIS';
export type Bucket =
  | 'A1' // Mobiliz var, km yaptı, rota açık
  | 'A2' // Mobiliz var, km yaptı, rota yok (rotasız hareket)
  | 'A3' // Mobiliz var, hareketsiz (km=0, kalite OK), rota açık
  | 'A4' // Mobiliz var, hareketsiz, rota yok (boşta)
  | 'A3K' // küçük hareket (0<km<minKm, kalite OK), rota açık
  | 'A4K' // küçük hareket, rota yok
  | 'A0' // veri yok / sinyal yok / km ERROR|UNKNOWN
  | 'A0P' // hareket doğrulanamadı (km<minKm ve kalite PARTIAL|ESTIMATED)
  | 'V0' // plaka/cihaz eşleşmesi belirsiz (AMBIGUOUS)
  | 'B1' // Mobiliz'de yok, şu an araçta yük var (takip dışı)
  | 'B1B' // Mobiliz'de yok, bugün yük taşıdı, şu an araçta yük yok
  | 'B2' // Mobiliz'de yok, rota açık, yük hiç yok
  | 'B3'; // Mobiliz'de yok, rotasız (atanmış/plan)
export type KmQuality = 'OK' | 'ESTIMATED' | 'PARTIAL' | 'ERROR' | 'UNKNOWN';
export type SignalState = 'LIVE' | 'STALE' | 'NONE';
export type MatchState = 'MATCHED' | 'AMBIGUOUS' | 'MULTI_DEVICE' | 'NONE';
export type AreaMatch = 'DEPOT' | 'AREA' | 'AMBIGUOUS' | 'NONE';
export type RowSource = 'ATAMA' | 'ROTA' | 'PLAN' | 'KONUM';
export type RowFlag =
  | 'DEVREDEN'
  | 'ERTESI_GUNE'
  | 'GELEN_TRANSFER'
  | 'MULTI_DEVICE'
  | 'AMBIGUOUS'
  | 'HIDDEN_VEHICLE'
  | 'PARTIAL'
  | 'BASKA_BOLGEDE_SEFER';
export type IndependentType = 'ARA_TRANSFER' | 'FTL' | 'DEDIKE' | 'YEDEK' | 'DIGER';
export type AssignmentMode = 'REGION' | 'INDEPENDENT';
export type AssignmentSource = 'MANUAL' | 'IMPORT_POINT_ID' | 'MOBILIZ_GROUP' | 'ROUTE_HISTORY' | 'LOCATION_HISTORY' | 'REQUEST';
export type NoteReason = 'BAKIM' | 'ARIZA' | 'KAZA' | 'YEDEK' | 'PLAN_DISI' | 'SOFOR_YOK' | 'IZINLI' | 'CIHAZ_ARIZASI' | 'DIGER'; // DIGER ⇒ note zorunlu
export type UserRef = { id: number; name: string } | null;
export type AlertsMode = 'off' | 'log' | 'notify';

export interface Counts {
  assigned: number; // açık ataması bu bölge olan araç (ATANMIS)
  visibleToday: number; // kartta görünen DISTINCT araç (tüm roller)
  routeOpened: number; // bugün bu bölgeye bağlı seferi olan DISTINCT araç
  mobilizActive: number; // activeNow=true (yalnız bugün; geçmiş günde null değil 0)
  kmDone: number; // A1+A2
  kmNotDone: number; // A3+A4+A3K+A4K
  unknown: number; // A0+A0P+V0
  untracked: number; // B1
  openAlerts: number; // bu bölgeyle ilgili OPEN+ACK uyarı
  missingReason: number; // A3/A4/A0/A0P/B3 olup elle sebebi girilmemiş
  byBucket: Record<Bucket, number>;
  byRole: Record<MembershipRole, number>;
}

export interface SessionSummary {
  sessionId: number;
  routeId: number;
  routeName: string;
  billType: 0 | 1 | 2; // 0 dağıtım, 1 toplama, 2 transfer
  role: RegionRole; // bu bölgeye göre rol
  status: 0 | 1 | 2; // 0 Oluşturuldu, 1 Taşımada, 2 Tamamlandı
  jobDay: number;
  autoTransfer: number;
  carriedOver: boolean; // devreden sefer
  onVehicleNow: number; // shipment_route.status=1 DISTINCT paket
  loadedToSession: number; // status IN (1,2,3) DISTINCT paket
  desiOnVehicle: number | null; // araçtaki paketlerin toplam desisi
  drivers: { id: number; name: string; type: 0 | 1 }[]; // 0 sürücü, 1 yardımcı
}

export interface AttendanceNote {
  reason: NoteReason;
  note: string | null;
  createdBy: UserRef;
  updatedAt: string;
  version: number;
}

export interface AttendanceRow {
  key: string; // "v:<vehicleId>" veya plakasız "s:<sessionId>"
  vehicleId: number | null;
  plate: string | null;
  membership: MembershipRole;
  assignedXdockId: number | null;
  assignedXdockName: string | null;
  independentType: IndependentType | null;
  sources: RowSource[];
  bucket: Bucket;
  mobiliz: {
    match: MatchState;
    networkId: string | null;
    signal: SignalState;
    lastDataTime: string | null;
    ignition: boolean | null;
    speed: number | null;
    activeNow: boolean | null; // yalnız bugün; geçmiş günde null
    address: string | null;
    areaMatch: AreaMatch | null;
    observedXdockId: number | null; // konuma göre bulunduğu bölge
  };
  km: {
    value: number | null;
    quality: KmQuality;
    workSeconds: number | null;
    idleSeconds: number | null;
    engineOnSeconds: number | null;
  };
  sessions: SessionSummary[]; // yalnız bu bölgeye bağlı seferler
  otherRegionSessionCount: number; // aynı gün başka bölgelerdeki sefer sayısı
  load: { onVehicleNow: number; onVehicleDesi: number | null; loadedToSession: number };
  plan: { planId: string; latestStatus: string | null } | null;
  note: AttendanceNote | null;
  alerts: { open: number; lastAlertId: string | null };
  flags: RowFlag[];
}

export type WarningType = 'PLAKASIZ_ROTA' | 'AMBIGUOUS_PLATE' | 'HIDDEN_VEHICLE_ROUTE' | 'UNMATCHED_DEVICE' | 'AMBIGUOUS_AREA';

export interface Warning {
  type: WarningType;
  xdockId: number | null;
  sessionId: number | null;
  vehicleId: number | null;
  plate: string | null;
  message: string;
}

export interface Quality {
  mobiliz: 'OK' | 'ESTIMATED' | 'ERROR' | 'NOT_CONFIGURED' | 'UNKNOWN';
  sessions: 'OK' | 'PARTIAL' | 'UNKNOWN'; // geçmiş günde gözlem yoksa UNKNOWN
  location: 'OK' | 'PARTIAL' | 'UNKNOWN';
}

export interface Assignment {
  id: string;
  vehicleId: number;
  plate: string;
  mode: AssignmentMode;
  xdockId: number | null;
  xdockName: string | null;
  independentType: IndependentType | null;
  allowedXdockIds: number[];
  source: AssignmentSource;
  validFrom: string;
  validTo: string | null;
  isOpen: boolean;
  note: string | null;
  createdBy: UserRef;
  closedBy: UserRef;
  version: number;
}

export type RequestAction = 'ASSIGN' | 'UNASSIGN' | 'INDEPENDENT';
export type RequestStatus = 'PENDING' | 'APPROVED' | 'REJECTED' | 'CANCELLED' | 'SUPERSEDED';

export interface AssignmentRequest {
  id: string;
  vehicleId: number;
  plate: string;
  action: RequestAction;
  targetXdockId: number | null;
  targetXdockName: string | null;
  independentType: IndependentType | null;
  reason: string;
  status: RequestStatus;
  requestedBy: UserRef;
  requestedAt: string;
  decidedBy: UserRef;
  decidedAt: string | null;
  decisionNote: string | null;
  currentAssignment: Assignment | null;
}

export type AlertStatus = 'OPEN' | 'ACK' | 'RESOLVED';
export type AlertResolvedReason =
  | null
  | 'BACK_IN_REGION'
  | 'SESSION_DELETED'
  | 'SESSION_VEHICLE_CHANGED'
  | 'ASSIGNMENT_CHANGED'
  | 'DAY_END'
  | 'MANUAL';

export interface Alert {
  id: string;
  vehicleId: number;
  plate: string;
  assignedXdockId: number;
  assignedXdockName: string;
  observedXdockId: number;
  observedXdockName: string;
  day: DayStr;
  trigger: 'ROUTE' | 'LOCATION';
  episode: number;
  status: AlertStatus;
  firstSeenAt: string;
  lastSeenAt: string;
  sessionIds: number[];
  routeNames: string[];
  ackBy: UserRef;
  ackAt: string | null;
  ackNote: string | null;
  resolvedAt: string | null;
  resolvedReason: AlertResolvedReason;
  mode: 'log' | 'notify'; // üretildiği andaki mod
  updatedAt: string;
}

// ───────────────────────── §2 Yoklama yanıtları ─────────────────────────
export interface MyRegionsResponse {
  regions: { xdockId: number; name: string }[];
  canSeeAll: boolean;
  canAssign: boolean;
  canRequest: boolean;
  canApprove: boolean;
  canNote: boolean;
  /** v1.2: XDOCK_ADMIN'in yönetici olduğu bölgeler (talep hedefi + uyarı ACK kapsamı); canSeeAll ⇒ null (tümü) */
  adminXdockIds: number[] | null;
  alertsMode: AlertsMode;
}

export interface OverviewResponse {
  date: DayStr;
  workday: number;
  computedAt: string;
  isToday: boolean;
  quality: Quality;
  regions: { xdockId: number; name: string; counts: Counts }[];
  pools: { independentSeen: number; unassignedSeen: number } | null; // yalnız canSeeAll
  warnings: Warning[];
}

export interface AttendanceRegion {
  xdockId: number;
  name: string;
  lat: number | null;
  lng: number | null;
  depotRadiusM: number; // v1.1: bölgenin depo yarıçapı (alan kaydı yoksa ayar varsayılanı)
  counts: Counts;
  rows: AttendanceRow[];
}

export interface AttendanceListResponse {
  date: DayStr;
  workday: number;
  computedAt: string;
  isToday: boolean;
  quality: Quality;
  regions: AttendanceRegion[];
  pools: { independent: AttendanceRow[]; unassigned: AttendanceRow[] } | null;
  warnings: Warning[];
}

export interface AttendanceListQuery {
  date: DayStr;
  xdockIds?: number[];
  includePools?: boolean;
}

export type DetailSession = SessionSummary & {
  createdAt: string | null;
  segments: { vehicleId: number; status: number; validFrom: string; validTo: string | null; closeReason: string | null }[];
  loadTimeline: { at: string; onVehicleNow: number; loadedToSession: number }[];
};

export interface CalendarDay {
  day: DayStr;
  bucket: Bucket | null;
  unknown: boolean;
  note: NoteReason | null;
}

export interface VehicleDetailResponse {
  date: DayStr;
  xdockId: number;
  row: AttendanceRow;
  vehicle: {
    id: number;
    plate: string;
    plate2: string | null;
    brand: string | null;
    model: string | null;
    categoryName: string | null;
    hidden: boolean;
    legacyPoint: { id: number; name: string } | null;
  };
  assignment: { current: Assignment | null; history: Assignment[] }; // XDOCK: yalnız kendi bölgesini içerenler
  sessions: DetailSession[];
  otherRegions: { xdockId: number; name: string; sessionCount: number }[] | null; // canSeeAll ise dolu, değilse null
  mobiliz: {
    lastPosition: {
      lat: number;
      lng: number;
      dataTime: string;
      speed: number | null;
      ignition: boolean | null;
      address: string | null;
    } | null;
    sampleCount: number;
    coveragePct: number | null;
  };
  calendar: CalendarDay[]; // son 30 gün, eski→yeni
  alerts: Alert[];
  pendingRequest: AssignmentRequest | null;
}

export interface VehicleRouteResponse {
  points: { lat: number; lng: number; at: string; speed: number | null; ignition: boolean | null }[];
  partial: boolean;
  gaps: { from: string; to: string }[];
}

export interface PutNoteBody {
  day: DayStr;
  vehicleId: number;
  xdockId: number;
  reason: NoteReason;
  note?: string | null;
  expectedVersion?: number | null;
}

export interface DeleteNoteQuery {
  day: DayStr;
  vehicleId: number;
  xdockId: number;
  expectedVersion: number;
}

// ───────────────────────── §3 Atama ─────────────────────────
export type AssignmentListMode = AssignmentMode | 'NONE';

export interface AssignmentListQuery {
  xdockId?: number;
  mode?: AssignmentListMode;
  q?: string;
  page: number; // 0 tabanlı
  limit: number;
}

export interface AssignmentListRow {
  vehicleId: number;
  plate: string;
  hidden: boolean;
  categoryName: string | null;
  assignment: Assignment | null;
  legacyPoint: { id: number; name: string } | null;
  last30: { xdockId: number; name: string; sessions: number }[]; // en çok 3, çoktan aza
  pendingRequest: AssignmentRequest | null;
}

export interface AssignmentListResponse {
  rows: AssignmentListRow[];
  total: number;
}

export interface AssignmentWriteItem {
  vehicleId: number;
  mode: AssignmentMode;
  xdockId?: number | null;
  independentType?: IndependentType | null;
  allowedXdockIds?: number[];
  note?: string | null;
  expectedVersion: number | null; // mevcut açık atama yoksa null
}

export interface AssignmentWriteResponse {
  ok: Assignment[];
  conflict: { vehicleId: number; currentVersion: number | null; message: string }[];
  failed: { vehicleId: number; message: string }[];
}

export interface RequestListQuery {
  status?: RequestStatus;
  xdockId?: number;
  page: number;
  limit: number;
}

export interface RequestListResponse {
  rows: AssignmentRequest[];
  total: number;
  pendingCount: number;
}

export interface CreateRequestBody {
  vehicleId: number;
  action: RequestAction;
  targetXdockId?: number | null;
  independentType?: IndependentType | null;
  reason: string; // 3..500
}

export type SuggestionSource = 'IMPORT_POINT_ID' | 'MOBILIZ_GROUP' | 'ROUTE_HISTORY' | 'LOCATION_HISTORY';

export interface SuggestionRow {
  vehicleId: number;
  plate: string;
  suggestedMode: AssignmentMode;
  suggestedXdockId: number | null;
  suggestedXdockName: string | null;
  independentType: IndependentType | null;
  source: AssignmentSource;
  confidence: number;
  evidence: string;
  currentAssignment: Assignment | null;
}

export interface SuggestionsResponse {
  rows: SuggestionRow[];
  generatedAt: string | null;
}

export interface SuggestionApplyItem {
  vehicleId: number;
  mode: AssignmentMode;
  xdockId?: number | null;
  independentType?: IndependentType | null;
  source: AssignmentSource;
  expectedVersion: number | null;
}

export interface AreaCity {
  cityId: number;
  cityName: string;
  subcityIds: number[] | null; // null = ilin tamamı
}

export interface AreaRegion {
  xdockId: number;
  name: string;
  lat: number | null;
  lng: number | null;
  coordValid: boolean;
  depotRadiusM: number;
  cities: AreaCity[];
  updatedAt: string | null;
  updatedBy: UserRef;
}

export interface AreaConflict {
  subcityId: number;
  subcityName: string;
  cityName: string;
  xdockIds: number[];
}

export interface AreasResponse {
  version: number;
  regions: AreaRegion[];
  conflicts: AreaConflict[];
}

export interface AreasGeoResponse {
  cities: { id: number; name: string; code: string; subcities: { id: number; name: string }[] }[];
}

export interface PutAreaBody {
  depotRadiusM: number; // 100..20000
  cities: { cityId: number; subcityIds: number[] | null }[];
  expectedVersion: number;
}

export interface AreaSeedResponse {
  proposed: { xdockId: number; cities: { cityId: number; subcityIds: number[] | null }[] }[];
  conflicts: AreaConflict[];
  applied: boolean;
}

export interface MobilizGroupRow {
  groupId: string;
  groupName: string;
  vehicleCount: number;
  xdockId: number | null;
  mode: AssignmentMode | null;
  independentType: IndependentType | null;
}

export interface MobilizGroupsResponse {
  rows: MobilizGroupRow[];
}

export interface MobilizGroupPutItem {
  groupId: string;
  xdockId: number | null;
  mode: AssignmentMode | null;
  independentType?: IndependentType | null;
}

export interface VehicleRegionSettings {
  alertsMode: AlertsMode;
  locationDwellMinutes: number;
  depotRadiusDefaultM: number;
  minKm: number;
  noteBackDays: number;
}

// ───────────────────────── §4 Uyarılar ─────────────────────────
export interface AlertListQuery {
  status?: string; // "OPEN,ACK"
  xdockId?: number;
  from?: DayStr;
  to?: DayStr;
  vehicleId?: number;
  q?: string; // v1.1: plaka araması (BE normalize eder)
  page: number;
  limit: number;
}

export interface AlertListResponse {
  rows: Alert[];
  total: number;
}

export interface AlertOpenCountResponse {
  open: number;
  ack: number;
  mode: AlertsMode;
  /** v1.3: açık uyarılar kaydın kendi moduna göre (mod değişim günü karışmasın) */
  openNotify?: number;
  openLog?: number;
}

// ───────────────────────── Etiketler (TR) ─────────────────────────
export const BUCKET_LABELS: Record<Bucket, string> = {
  A1: 'Normal çalıştı',
  A2: 'Rotasız hareket',
  A3: 'Rota açık, hareketsiz',
  A4: 'Boşta',
  A3K: 'Küçük hareket (rota açık)',
  A4K: 'Küçük hareket (rota yok)',
  A0: 'Veri yok / belirsiz',
  A0P: 'Hareket doğrulanamadı',
  V0: 'Eşleşme belirsiz',
  B1: "Takip dışı (Mobiliz'siz, yüklü)",
  B1B: "Mobiliz'siz, bugün yük taşıdı",
  B2: "Mobiliz'siz rota (yük yok)",
  B3: "Mobiliz'siz, rotasız"
};

/** Kart kenarlığı / takvim rengi */
export const BUCKET_COLORS: Record<Bucket, string> = {
  A1: '#16a34a',
  A2: '#0ea5e9',
  A3: '#dc2626',
  A4: '#f97316',
  A3K: '#f59e0b',
  A4K: '#fbbf24',
  A0: '#94a3b8',
  A0P: '#a78bfa',
  V0: '#8b5cf6',
  B1: '#b91c1c',
  B1B: '#ec4899',
  B2: '#e11d48',
  B3: '#64748b'
};

/** BE satır sıralamasıyla aynı (B1 → A3 → A0/A0P → V0 → A4 → B2 → B3 → A3K/A4K → A2 → A1 → B1B) */
export const BUCKET_ORDER: Bucket[] = ['B1', 'A3', 'A0', 'A0P', 'V0', 'A4', 'B2', 'B3', 'A3K', 'A4K', 'A2', 'A1', 'B1B'];

export const MEMBERSHIP_LABELS: Record<MembershipRole, string> = {
  ATANMIS: 'Atanmış',
  MISAFIR: 'Misafir',
  BAGIMSIZ: 'Bağımsız',
  ATANMAMIS: 'Atanmamış'
};

export const REGION_ROLE_LABELS: Record<RegionRole, string> = {
  DAGITIM: 'Dağıtım',
  TOPLAMA: 'Toplama',
  GIDEN_TRANSFER: 'Giden transfer',
  GELEN_TRANSFER: 'Gelen transfer'
};

export const SESSION_STATUS_LABELS: Record<0 | 1 | 2, string> = {
  0: 'Oluşturuldu',
  1: 'Taşımada',
  2: 'Tamamlandı'
};

export const INDEPENDENT_TYPE_LABELS: Record<IndependentType, string> = {
  ARA_TRANSFER: 'Ara transfer',
  FTL: 'FTL',
  DEDIKE: 'Dedike',
  YEDEK: 'Yedek',
  DIGER: 'Diğer'
};

export const ASSIGNMENT_SOURCE_LABELS: Record<AssignmentSource, string> = {
  MANUAL: 'Elle',
  IMPORT_POINT_ID: 'Eski kayıt noktası',
  MOBILIZ_GROUP: 'Mobiliz grubu',
  ROUTE_HISTORY: 'Rota geçmişi',
  LOCATION_HISTORY: 'Konum geçmişi',
  REQUEST: 'Talep'
};

export const NOTE_REASON_LABELS: Record<NoteReason, string> = {
  BAKIM: 'Bakım',
  ARIZA: 'Arıza',
  KAZA: 'Kaza',
  YEDEK: 'Yedek',
  PLAN_DISI: 'Plan dışı',
  SOFOR_YOK: 'Şoför yok',
  IZINLI: 'İzinli',
  CIHAZ_ARIZASI: 'Cihaz arızası (Mobiliz)',
  DIGER: 'Diğer'
};

export const NOTE_REASONS: NoteReason[] = ['BAKIM', 'ARIZA', 'KAZA', 'YEDEK', 'PLAN_DISI', 'SOFOR_YOK', 'IZINLI', 'CIHAZ_ARIZASI', 'DIGER'];

/** Sebep seçimi dört grup + Diğer (kullanıcı kararı §13.2); "Diğer" seçilince açıklama zorunlu */
export const NOTE_REASON_GROUPS: { label: string; reasons: NoteReason[] }[] = [
  { label: 'Araç', reasons: ['BAKIM', 'ARIZA', 'KAZA', 'YEDEK'] },
  { label: 'Plan', reasons: ['PLAN_DISI'] },
  { label: 'Sürücü', reasons: ['SOFOR_YOK', 'IZINLI'] },
  { label: 'Cihaz', reasons: ['CIHAZ_ARIZASI'] },
  { label: 'Diğer', reasons: ['DIGER'] }
];

/** Elle sebep beklenen kovalar (Counts.missingReason tanımı) */
export const REASON_EXPECTED_BUCKETS: Bucket[] = ['A3', 'A4', 'A0', 'A0P', 'B3'];

export const ROW_FLAG_LABELS: Record<RowFlag, string> = {
  DEVREDEN: 'Devreden',
  ERTESI_GUNE: 'Ertesi güne aktarılacak',
  GELEN_TRANSFER: 'Gelen transfer',
  MULTI_DEVICE: 'Çoklu cihaz',
  AMBIGUOUS: 'Eşleşme belirsiz',
  HIDDEN_VEHICLE: 'Gizli araç',
  PARTIAL: 'Kısmi veri',
  BASKA_BOLGEDE_SEFER: 'Başka bölgede sefer'
};

export const KM_QUALITY_LABELS: Record<KmQuality, string> = {
  OK: 'Kesin',
  ESTIMATED: 'Tahmini',
  PARTIAL: 'Kısmi',
  ERROR: 'Hata',
  UNKNOWN: 'Bilinmiyor'
};

export const WARNING_TYPE_LABELS: Record<WarningType, string> = {
  PLAKASIZ_ROTA: 'Plakasız rotalar',
  AMBIGUOUS_PLATE: 'Belirsiz plaka eşleşmeleri',
  HIDDEN_VEHICLE_ROUTE: 'Gizli araçla açılan rotalar',
  UNMATCHED_DEVICE: 'Eşleşmeyen Mobiliz cihazları',
  AMBIGUOUS_AREA: 'Belirsiz bölge alanları'
};

export const ALERT_STATUS_LABELS: Record<AlertStatus, string> = {
  OPEN: 'Açık',
  ACK: 'Görüldü',
  RESOLVED: 'Kapandı'
};

export const ALERT_TRIGGER_LABELS: Record<Alert['trigger'], string> = {
  ROUTE: 'Rota',
  LOCATION: 'Konum'
};

export const ALERT_RESOLVED_REASON_LABELS: Record<Exclude<AlertResolvedReason, null>, string> = {
  BACK_IN_REGION: 'Bölgesine döndü',
  SESSION_DELETED: 'Sefer silindi',
  SESSION_VEHICLE_CHANGED: 'Seferin aracı değişti',
  ASSIGNMENT_CHANGED: 'Atama değişti',
  DAY_END: 'Gün bitti',
  MANUAL: 'Elle kapatıldı'
};

export const REQUEST_ACTION_LABELS: Record<RequestAction, string> = {
  ASSIGN: 'Bölgeye ata',
  UNASSIGN: 'Bölgeden çıkar',
  INDEPENDENT: 'Bağımsız yap'
};

export const REQUEST_STATUS_LABELS: Record<RequestStatus, string> = {
  PENDING: 'Bekliyor',
  APPROVED: 'Onaylandı',
  REJECTED: 'Reddedildi',
  CANCELLED: 'İptal edildi',
  SUPERSEDED: 'Geçersiz (atama değişti)'
};

export const SUGGESTION_SOURCE_LABELS: Record<SuggestionSource, string> = {
  IMPORT_POINT_ID: 'Eski kayıt noktası (point_id)',
  MOBILIZ_GROUP: 'Mobiliz grubu',
  ROUTE_HISTORY: 'Rota geçmişi (30 gün)',
  LOCATION_HISTORY: 'Konum geçmişi (14 gün)'
};

export const ALERTS_MODE_LABELS: Record<AlertsMode, string> = {
  off: 'Kapalı',
  log: 'Deneme (kayıt) — bildirim yok',
  notify: 'Bildirim açık'
};
